import * as React from 'react';

import {CHANNELS} from 'nutshell-core/channels/consts';

import {FetchMoreButton} from 'shells/table/fetch-more-button';
import {LegacySetupTableEmptyState} from 'shells/table/legacy-setup-table-empty-state';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';
import {SetupItemTable} from 'shells/table/setup-item-table';
import type {Row} from 'shells/table/types';

import type {ChannelType} from 'nutshell-graphql-types';

import {getHelpInfoFromChannelType, getIconFromChannelType} from '../../../core/sources/helpers';

import type {SourceValues} from '../types';
import {useGetSources} from '../use-get-sources';

import {getDisplayableType, getReportUrl} from './helpers';
import {SourceRowActionMenu} from './source-row-action-menu';

type Props = {
    channel: ChannelType;
    onAddClick: () => void | null | undefined;
    onEditClick: (input: SourceValues) => void;
    onDelete: (arg1: {id: string; channel: ChannelType | null | undefined}) => void;
};

export function SourcesByChannelTableWrapper(props: Props) {
    const {sources, totalCount, isLoading, error, hasNextPage, fetchMore} = useGetSources({
        channel: props.channel,
    });
    const title = CHANNELS[props.channel].title;
    const description = CHANNELS[props.channel].description;
    const icon = getIconFromChannelType(props.channel);
    const helpInfo = getHelpInfoFromChannelType(props.channel);

    const handleDeleteSource = (sourceId: string) => {
        props.onDelete({
            id: sourceId,
            channel: null,
        });
    };

    const renderRowActionMenu = (row: Row<any>) => (
        <SourceRowActionMenu
            name={row.original.name}
            // @ts-expect-error
            onDeleteSource={
                row.original.isDeleteable ? () => handleDeleteSource(row.original.id) : undefined
            }
            reportUrl={getReportUrl(row.original.id)}
        />
    );

    return (
        <LegacySetupTableWrapper
            title={title}
            subtitle={description}
            icon={icon}
            helpInfo={helpInfo}
            count={totalCount}
        >
            <SetupItemTable
                items={sources.map((source) => ({
                    id: source.id,
                    name: source.name,
                    type: getDisplayableType(source.type),
                    isDeleteable: source.isDeleteable,
                    isEditable: source.isEditable,
                    createdTime: source.createdTime,
                    reportUrl: getReportUrl(source.id),
                    lastUsedTime: source.lastUsedTime,
                }))}
                title='Source'
                isLoading={isLoading}
                error={error}
                canLoadMore={hasNextPage}
                onAddRowClick={props.onAddClick}
                addButtonText={`Add ${title} source`}
                onSelectRow={(row: Row<any>) => {
                    const name = row.original.name;
                    const selectedSource = sources.find((source) => source.name === name);

                    if (selectedSource) {
                        props.onEditClick({
                            id: selectedSource.id,
                            name,
                            channel: props.channel,
                        });
                    }
                }}
                renderRowActionMenu={renderRowActionMenu}
                emptyState={
                    <LegacySetupTableEmptyState
                        text={
                            <>
                                No sources in <strong>{title}</strong>
                            </>
                        }
                    />
                }
                additionalColumns={React.useMemo(
                    () => [
                        {
                            column: {
                                id: 'type',
                                Header: 'Type',
                                // @ts-expect-error
                                accessor: (row) => row.type,
                                dataType: 'component',
                                width: 150,
                                Cell: ({cell}: any) => (
                                    <span className='light-text'>{cell.value}</span>
                                ),
                            },
                            index: 1,
                        },
                    ],
                    []
                )}
            />
            {hasNextPage ? (
                <FetchMoreButton buttonText='See all sources' onClick={fetchMore} />
            ) : undefined}
        </LegacySetupTableWrapper>
    );
}
